import { useState, useEffect, useCallback } from "react";
import { api, clearStoredAuth, CRM_AUTH_CLEARED_EVENT, getStoredAuth, setStoredAuth, type CrmSection } from "@/lib/api";

export interface CrmUser {
  id: string;
  username: string;
  role: "admin" | "manager";
  permissions: CrmSection[];
  canManageUsers: boolean;
  created_at: string;
}

function getStoredUser(): CrmUser | null {
  const auth = getStoredAuth();
  if (!auth) return null;
  return {
    id: auth.user.id,
    username: auth.user.login,
    role: auth.user.role,
    permissions: auth.user.access_sections?.length
      ? auth.user.access_sections
      : auth.user.role === "admin"
        ? ["catalog", "orders", "requests", "analytics", "users"]
        : ["catalog", "orders"],
    canManageUsers: auth.user.can_manage_users ?? auth.user.login === "voldan",
    created_at: new Date().toISOString(),
  };
}

export function useAuth() {
  const [user, setUser] = useState<CrmUser | null>(getStoredUser);
  const [loading, setLoading] = useState(false);
  const isAuthenticated = !!user;

  useEffect(() => {
    setLoading(false);

    const handleAuthCleared = () => setUser(null);
    globalThis.addEventListener?.(CRM_AUTH_CLEARED_EVENT, handleAuthCleared);
    return () => globalThis.removeEventListener?.(CRM_AUTH_CLEARED_EVENT, handleAuthCleared);
  }, []);

  const login = useCallback(
    async (username: string, password: string): Promise<{ error?: string }> => {
      if (!username.trim() || !password.trim()) {
        return { error: "Введите логин и пароль" };
      }
      try {
        const auth = await api.login(username.trim(), password.trim());
        setStoredAuth(auth);
        setUser(getStoredUser());
        return {};
      } catch (err) {
        return { error: err instanceof Error ? err.message : "Не удалось войти" };
      }
    },
    []
  );

  const logout = useCallback(() => {
    clearStoredAuth();
    setUser(null);
  }, []);

  return { user, loading, isAuthenticated, login, logout };
}
